import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useState, useEffect } from 'react'
import { getActiveSubscription, isActiveTrial, getTrialDaysRemaining } from '../services/subscriptionService'
import TrialExpiredPaywall from '../components/TrialExpiredPaywall'
import { supabase } from '../lib/supabase'
import { 
  getUserNotifications, 
  getUnreadCount, 
  markAsRead as markNotificationAsRead,
  markAllAsRead as markAllNotificationsAsRead,
  deleteNotification,
  subscribeToNotifications,
  unsubscribeFromNotifications,
  formatNotificationTime,
  getNotificationIcon,
  getNotificationColor
} from '../services/notificationService'
import logo from '../assets/logo.png'

export default function DashboardLayout() {
  const { user, signOut, userProfile, loadUserProfile, setUserProfile: storeSetUserProfile } = useAuthStore()
  const navigate = useNavigate()
  const [darkMode, setDarkMode] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [currentPlan, setCurrentPlan] = useState('Free')
  const [subscriptionData, setSubscriptionData] = useState(null)
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loadingNotifications, setLoadingNotifications] = useState(false)
  const [notificationChannel, setNotificationChannel] = useState(null)
  const [profileChannel, setProfileChannel] = useState(null)

  useEffect(() => {
    const isDark = localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)
    setDarkMode(isDark)
    if (isDark) {
      document.documentElement.classList.add('dark')
    }
    
    // Load current subscription
    loadSubscription()

    // Load notifications and setup real-time subscription
    if (user?.id) {
      loadNotifications()
      loadUnreadCount()
      setupNotificationSubscription()
      loadUserProfile()
      setupProfileSubscription()
    }

    // Keyboard shortcut for search (Cmd/Ctrl + F)
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        document.querySelector('input[type="text"][placeholder*="Search"]')?.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      // Cleanup notification subscription
      if (notificationChannel) {
        unsubscribeFromNotifications(notificationChannel)
      }
      // Cleanup profile subscription
      if (profileChannel) {
        supabase.removeChannel(profileChannel)
      }
    }
  }, [user])

  const loadNotifications = async () => {
    if (!user?.id) return
    
    setLoadingNotifications(true)
    try {
      const { data, error } = await getUserNotifications(user.id, { limit: 20 })
      if (!error && data) {
        setNotifications(data)
      }
    } catch (err) {
      console.error('Error loading notifications:', err)
    } finally {
      setLoadingNotifications(false)
    }
  }

  const loadUnreadCount = async () => {
    if (!user?.id) return
    
    try {
      const { count } = await getUnreadCount(user.id)
      setUnreadCount(count)
    } catch (err) {
      console.error('Error loading unread count:', err)
    }
  }

  const setupProfileSubscription = () => {
    if (!user?.id) return

    const channel = supabase
      .channel('user-profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          // Update profile when it changes in real-time
          if (payload.new) {
            storeSetUserProfile({
              full_name: payload.new.full_name,
              avatar_url: payload.new.avatar_url
            })
          }
        }
      )
      .subscribe()

    setProfileChannel(channel)
  }

  const setupNotificationSubscription = () => {
    if (!user?.id) return

    const channel = subscribeToNotifications(user.id, (newNotification) => {
      // Add new notification to the list
      setNotifications(prev => [newNotification, ...prev])
      // Update unread count
      setUnreadCount(prev => prev + 1)
      
      // Optional: Show browser notification if permitted
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(newNotification.title, {
          body: newNotification.message,
          icon: '/logo.png',
          badge: '/logo.png'
        })
      }
    })

    setNotificationChannel(channel)
  }

  const loadSubscription = async () => {
    try {
      const subscription = await getActiveSubscription()
      setSubscriptionData(subscription)
      if (subscription) {
        const planName = subscription.plan_name || 'free'
        setCurrentPlan(
          subscription.is_trial
            ? 'Trial'
            : planName.charAt(0).toUpperCase() + planName.slice(1)
        )
      }
    } catch (err) {
      console.error('Error loading subscription:', err)
    } finally {
      setSubscriptionLoaded(true)
    }
  }

  const toggleDarkMode = () => {
    const newMode = !darkMode
    setDarkMode(newMode)
    if (newMode) {
      document.documentElement.classList.add('dark')
      localStorage.theme = 'dark'
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.theme = 'light'
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setSidebarOpen(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      console.log('Searching for:', searchQuery)
      // يمكن إضافة منطق البحث هنا
      navigate(`/projects?search=${encodeURIComponent(searchQuery)}`)
    }
  }

  const markAsRead = async (notificationId) => {
    try {
      const { error } = await markNotificationAsRead(notificationId)
      if (!error) {
        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        )
        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (err) {
      console.error('Error marking notification as read:', err)
    }
  }

  const markAllAsRead = async () => {
    if (!user?.id) return
    
    try {
      const { error } = await markAllNotificationsAsRead(user.id)
      if (!error) {
        // Update local state
        setNotifications(prev => prev.map(n => ({ ...n, read: true })))
        // Reset unread count
        setUnreadCount(0)
      }
    } catch (err) {
      console.error('Error marking all as read:', err)
    }
  }

  const handleDeleteNotification = async (notificationId, e) => {
    e?.stopPropagation()
    
    try {
      const { error } = await deleteNotification(notificationId)
      if (!error) {
        // Update local state
        const notification = notifications.find(n => n.id === notificationId)
        setNotifications(prev => prev.filter(n => n.id !== notificationId))
        
        // Update unread count if it was unread
        if (notification && !notification.read) {
          setUnreadCount(prev => Math.max(0, prev - 1))
        }
      }
    } catch (err) {
      console.error('Error deleting notification:', err)
    }
  }

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markAsRead(notification.id)
    }

    // Navigate to action URL if exists
    if (notification.action_url) {
      navigate(notification.action_url)
      setShowNotifications(false)
    }
  }

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission()
    }
  }

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission()
  }, [])

  const navigation = [
    { name: 'Dashboard', to: '/dashboard', icon: 'dashboard' },
    { name: 'Projects', to: '/projects', icon: 'folder' },
    { name: 'Team', to: '/team', icon: 'group' },
    { name: 'Notifications', to: '/notifications', icon: 'notifications', badge: unreadCount },
    { name: 'Invitations', to: '/invitations', icon: 'mail' },
    { name: 'Usage', to: '/usage', icon: 'analytics' },
    { name: 'Subscriptions', to: '/subscriptions', icon: 'payments' },
  ]

  const supportNavigation = [
    { name: 'Settings', to: '/settings', icon: 'settings' },
    { name: 'Security', to: '/security', icon: 'verified_user' },
    { name: 'Help', to: '/help', icon: 'help' },
  ]

  const location = useLocation()
  const trialActive = isActiveTrial(subscriptionData)
  const trialDaysLeft = getTrialDaysRemaining(subscriptionData)
  const isExpired = subscriptionLoaded && !subscriptionData

  // Allow access to /subscriptions even when expired so the user can upgrade
  if (isExpired && location.pathname !== '/subscriptions') {
    return <TrialExpiredPaywall isTrialExpired={true} />
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-50
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        w-64 bg-surface-light dark:bg-surface-dark border-r border-border-light dark:border-border-dark 
        flex flex-col transition-all duration-200 md:rounded-r-2xl
      `}>
        {/* Logo - Fixed at top */}
        <div className="h-16 flex items-center px-6 border-b border-border-light dark:border-border-dark flex-shrink-0">
          <div className="flex items-center gap-3">
            <img src={logo} alt="ProofFlow Logo" className="w-8 h-8 rounded-full object-cover shadow-lg" />
            <span className="font-bold text-lg tracking-tight text-text-light dark:text-text-dark">ProofFlow</span>
          </div>
        </div>

        {/* Scrollable Navigation Container */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 
          [&::-webkit-scrollbar]:w-2 
          [&::-webkit-scrollbar-track]:bg-transparent 
          [&::-webkit-scrollbar-thumb]:bg-gray-300 
          [&::-webkit-scrollbar-thumb]:rounded-full
          dark:[&::-webkit-scrollbar-thumb]:bg-gray-700
          hover:[&::-webkit-scrollbar-thumb]:bg-gray-400
          dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-600">
          {/* Main Navigation */}
          <nav className="px-4 space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.to}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-primary text-white dark:bg-primary dark:text-white shadow-lg shadow-primary/20'
                      : 'text-text-muted-light dark:text-text-muted-dark hover:bg-gray-100 dark:hover:bg-white/5 hover:text-text-light dark:hover:text-white'
                  }`
                }
              >
                <span className="material-symbols-outlined text-xl">{item.icon}</span>
                <span className="font-medium text-sm flex-1">{item.name}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium min-w-[20px] text-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </NavLink>
            ))}

            {/* Support Section */}
            <div className="pt-4 mt-4 border-t border-border-light dark:border-border-dark">
              <p className="px-4 text-[10px] font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-[0.1em] mb-2">
                Support
              </p>
              {supportNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.to}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? 'bg-primary text-white dark:bg-primary dark:text-white shadow-lg shadow-primary/20'
                        : 'text-text-muted-light dark:text-text-muted-dark hover:bg-gray-100 dark:hover:bg-white/5 hover:text-text-light dark:hover:text-white'
                    }`
                  }
                >
                  <span className="material-symbols-outlined text-xl">{item.icon}</span>
                  <span className="font-medium text-sm">{item.name}</span>
                </NavLink>
              ))}
              
              {/* Logout Button */}
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 mt-1"
              >
                <span className="material-symbols-outlined text-xl">logout</span>
                <span className="font-medium text-sm">Sign Out</span>
              </button>
            </div>
          </nav>
        </div>

        {/* User Profile - Fixed at bottom */}
        <div className="p-4 border-t border-border-light dark:border-border-dark flex-shrink-0">
          <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-3 flex items-center gap-3 border border-transparent dark:border-border-dark">
            <div className="h-8 w-8 rounded-full overflow-hidden shrink-0 shadow-lg ring-2 ring-primary/20">
              {userProfile?.avatar_url ? (
                <img
                  src={userProfile.avatar_url}
                  alt={userProfile.full_name || user?.email || 'User'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'
                  }}
                />
              ) : null}
              <div
                className="w-full h-full bg-primary flex items-center justify-center text-white text-xs font-bold"
                style={{ display: userProfile?.avatar_url ? 'none' : 'flex' }}
              >
                {userProfile?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'A'}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                {userProfile?.full_name || user?.email}
              </p>
              <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                {currentPlan} Plan
              </p>
            </div>
            <button
              onClick={handleSignOut}
              className="text-text-muted-light dark:text-text-muted-dark hover:text-primary dark:hover:text-white transition-colors"
              title="Sign out"
            >
              <span className="material-symbols-outlined text-lg">logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-y-auto bg-background-light dark:bg-background-dark">
        {/* Trial Banner */}
        {trialActive && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-2.5 flex items-center justify-between gap-4">
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
              ⏳ {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left in your free trial
            </p>
            <button
              onClick={() => navigate('/billing')}
              className="text-xs font-bold text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700 px-3 py-1 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors whitespace-nowrap"
            >
              Upgrade now →
            </button>
          </div>
        )}
        {/* Header */}
        <header className="h-16 bg-surface-light dark:bg-surface-dark border-b border-border-light dark:border-border-dark flex items-center justify-between px-8 sticky top-0 z-10 rounded-br-2xl">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 rounded-lg text-text-muted-light dark:text-text-muted-dark hover:bg-gray-100 dark:hover:bg-white/5 transition-all"
          >
            <span className="material-symbols-outlined">menu</span>
          </button>

          {/* Search Bar - Hidden on mobile */}
          <form onSubmit={handleSearch} className="hidden md:flex items-center bg-gray-50 dark:bg-white/5 rounded-xl px-4 py-2 w-96 border border-transparent dark:border-border-dark focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-all">
            <span className="material-symbols-outlined text-text-muted-light dark:text-text-muted-dark text-xl mr-2">
              search
            </span>
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-full text-text-light dark:text-white placeholder-text-muted-light dark:placeholder-text-muted-dark focus:ring-0 p-0"
            />
            <span className="text-[10px] text-text-muted-light dark:text-text-muted-dark border border-border-light dark:border-border-dark rounded px-1.5 py-0.5 ml-2">
              ⌘F
            </span>
          </form>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-text-muted-light dark:text-text-muted-dark relative transition-all"
              >
                <span className="material-symbols-outlined">notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-surface-light dark:border-surface-dark"></span>
                )}
              </button>
              
              {/* Notifications Dropdown */}
              {showNotifications && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowNotifications(false)}
                  />
                  <div className="absolute right-0 mt-2 w-96 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl shadow-lg z-50 overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-border-light dark:border-border-dark flex items-center justify-between bg-gradient-to-r from-primary/5 to-transparent">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-text-light dark:text-white">Notifications</h3>
                        {unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button 
                            onClick={markAllAsRead}
                            className="text-xs text-primary hover:text-primary-hover dark:hover:text-white font-medium transition-colors"
                          >
                            Mark all read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button
                            onClick={() => loadNotifications()}
                            className="p-1 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-all"
                            title="Refresh notifications"
                          >
                            <span className="material-symbols-outlined text-sm">refresh</span>
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                      {loadingNotifications ? (
                        <div className="p-8 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="p-12 text-center">
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                            <span className="material-symbols-outlined text-4xl text-text-muted-light dark:text-text-muted-dark">notifications_none</span>
                          </div>
                          <p className="text-sm text-text-muted-light dark:text-text-muted-dark">No notifications yet</p>
                          <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">You're all caught up!</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`group relative p-4 border-b border-border-light dark:border-border-dark hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-all ${
                              !notification.read ? 'bg-primary/5 dark:bg-primary/10' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Icon */}
                              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shrink-0 ${
                                !notification.read ? 'ring-2 ring-primary ring-offset-2 ring-offset-surface-light dark:ring-offset-surface-dark' : ''
                              }`}>
                                <span className={`material-symbols-outlined text-lg ${getNotificationColor(notification.type)}`}>
                                  {getNotificationIcon(notification.type)}
                                </span>
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className={`text-sm font-medium mb-1 ${
                                    !notification.read 
                                      ? 'text-text-light dark:text-white' 
                                      : 'text-text-muted-light dark:text-text-muted-dark'
                                  }`}>
                                    {notification.title}
                                  </p>
                                  {!notification.read && (
                                    <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1"></div>
                                  )}
                                </div>
                                
                                <p className="text-xs text-text-muted-light dark:text-text-muted-dark mb-2 line-clamp-2">
                                  {notification.message}
                                </p>
                                
                                {/* Footer */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-text-muted-light dark:text-text-muted-dark">
                                      {formatNotificationTime(notification.created_at)}
                                    </span>
                                    {notification.action_label && (
                                      <>
                                        <span className="text-xs text-text-muted-light dark:text-text-muted-dark">•</span>
                                        <span className="text-xs text-primary font-medium">
                                          {notification.action_label}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  
                                  {/* Delete Button */}
                                  <button
                                    onClick={(e) => handleDeleteNotification(notification.id, e)}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                                    title="Delete notification"
                                  >
                                    <span className="material-symbols-outlined text-sm text-red-500">delete</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                      <div className="p-3 border-t border-border-light dark:border-border-dark bg-gray-50 dark:bg-white/5">
                        <button
                          onClick={() => {
                            navigate('/notifications')
                            setShowNotifications(false)
                          }}
                          className="w-full text-center text-sm text-primary hover:text-primary-hover dark:hover:text-white font-medium transition-colors"
                        >
                          View all notifications
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-text-muted-light dark:text-yellow-400 transition-all"
              title="Toggle dark mode"
            >
              <span className="material-symbols-outlined">{darkMode ? 'light_mode' : 'dark_mode'}</span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-grow">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
